/**
 * Whether anything has used this connection's key since somebody changed it.
 *
 * On 2026-10-11 dev's TIM refused every call, a new token was set from this
 * page, and the card went on saying "down": its status is built on calls that
 * happened, and all of them were older than the token. Pressing Test changed
 * nothing, because TIM's Test is a health check and presents no key.
 *
 * So the card says, only in that state, that the key has not been used since
 * it was changed, and whether Test can check it.
 *
 * ONE CLOCK. Both times come from the service, so they are comparable.
 */

export interface KeyFreshness {
  /** When the key stored for this lane was last written, epoch seconds.
   *  Absent when no key has ever been typed here, in which case the
   *  deployment's value is in use and nobody has changed anything. */
  updatedAt?: number | null
  /** When this lane last made a real call, epoch seconds, from the same
   *  answers the verdict is built on. Null when it has made none inside that
   *  window.
   *
   *  NEVER `observed_at`. That is when the probe looked, the probe presents no
   *  credential, and pressing Test moves it: the line would disappear for the
   *  exact action that proves nothing about the key, which is the mistake this
   *  exists to prevent. */
  lastCallAt?: number | null
  /** Whether the key has been proved from this page since it was set. A proof
   *  DOES present the key, so it answers the question this line asks. It is
   *  page state and not a stored fact, so a reload brings the line back; that
   *  is honest, because nothing server side has recorded a proof. */
  proved?: boolean
  what?: string
  /** Whether this connection's Test presents the key. TIM's does not. */
  testPresentsIt?: boolean
}

// THE BUTTON'S OWN LABEL, so the line names what is on the screen. A sentence
// telling somebody to press "Prove the key" beside a button reading "Prove
// this key" is a reader looking for a control that is not there, and
// `Settings.keyfresh.test.tsx` pins the two together.
/** `what` is "key" or "token". */
export const NOT_USED_SINCE = (what: string, testPresentsIt: boolean) =>
  `This ${what} has not been used since it was changed. ` +
  (testPresentsIt ? 'Press Test to check it.' : 'The next card checks it: Test does not present it.')

export function keyAwaitsACall(freshness: KeyFreshness): string | null {
  const { updatedAt, lastCallAt, proved } = freshness
  if (!updatedAt) return null
  if (proved) return null
  if (!lastCallAt || updatedAt > lastCallAt) {
    return NOT_USED_SINCE(freshness.what ?? 'key', freshness.testPresentsIt ?? true)
  }
  return null
}
